import { ILink, ModelTypes } from "@types";

import { SLUGS } from "consts";
import { getPublicLocale, getSiteUrlForLocale, Locale } from "consts/locale";
import { getPathname, routing } from "i18n/routing";

import { getCmsLocale } from "./locale";
import { appendOfficeContactQueryToHref, type OfficeContactPrefillParams } from "./officeContact";
import { getOfficeParentPathPrefix } from "./officePath";
const routePathnames = new Set(Object.keys(routing.pathnames));

// Overview/singleton record types where the base path IS the full path - no slug should be appended
const OVERVIEW_RECORD_TYPES = new Set([
  ModelTypes.HomepageRecord,
  ModelTypes.ArticlesOverviewRecord,
  ModelTypes.ServicesOverviewRecord,
  ModelTypes.ExpertisesOverviewRecord,
  ModelTypes.ForWhoOverviewRecord,
  ModelTypes.JobListingOverviewRecord,
  ModelTypes.NearbyOverviewRecord,
  ModelTypes.AccountanciesOverviewRecord,
  ModelTypes.TaxesOverviewRecord,
  ModelTypes.SearchRecord,
]);

/** True for singleton overview pages (localized path comes from routing, not CMS slug segments). */
export const isOverviewSingletonRecord = (typename: string | undefined): boolean =>
  Boolean(typename && OVERVIEW_RECORD_TYPES.has(typename as ModelTypes));

const BASE_PATH_BY_TYPE: Partial<Record<string, string>> = {
  [ModelTypes.HomepageRecord]: SLUGS.home,
  [ModelTypes.ServiceRecord]: SLUGS.services,
  [ModelTypes.ServicesOverviewRecord]: SLUGS.services,
  [ModelTypes.OfficeRecord]: SLUGS.offices,
  [ModelTypes.ExpertiseRecord]: SLUGS.expertises,
  [ModelTypes.ExpertisesOverviewRecord]: SLUGS.expertises,
  [ModelTypes.ArticleRecord]: SLUGS.articles,
  [ModelTypes.ArticlesOverviewRecord]: SLUGS.articles,
  [ModelTypes.AuthorRecord]: SLUGS.author,
  [ModelTypes.ReviewerRecord]: SLUGS.reviewer,
  [ModelTypes.MunicipalityRecord]: SLUGS.municipalities,
  // Pages live at /[...slug] directly, same root as home
  [ModelTypes.PageRecord]: SLUGS.home,
  [ModelTypes.CareerRecord]: SLUGS.career,
  [ModelTypes.JobListingOverviewRecord]: SLUGS.jobs,
  [ModelTypes.JobListingLayoutRecord]: SLUGS.jobs,
  [ModelTypes.ContactRecord]: SLUGS.contact,
  [ModelTypes.SearchRecord]: SLUGS.search,
  [ModelTypes.TaxesOverviewRecord]: SLUGS.audit,
  [ModelTypes.AuditPageRecord]: SLUGS.audit,
  [ModelTypes.AccountanciesOverviewRecord]: SLUGS.accountancy,
  [ModelTypes.NearbyOverviewRecord]: SLUGS.nearby,
  [ModelTypes.ForWhoOverviewRecord]: SLUGS.targetGroups,
  [ModelTypes.TargetGroupRecord]: SLUGS.targetGroups,
};

// Map model types to their base paths
export const getBasePathForType = (typename: string): string => BASE_PATH_BY_TYPE[typename] ?? "";

const BASE_PATH_BY_COLLECTION: Record<string, string> = {
  allOffices: SLUGS.offices,
  allServices: SLUGS.services,
  allArticles: SLUGS.articles,
  allAuthors: SLUGS.author,
  allReviewers: SLUGS.reviewer,
  allContacts: SLUGS.contact,
  allExpertises: SLUGS.expertises,
  allMunicipalities: SLUGS.nearby,
  allAuditPages: SLUGS.audit,
  allTargetGroups: SLUGS.targetGroups,
  // Pages live at /[...slug] directly
  allPages: SLUGS.home,
};

// Map GraphQL collection names to their base paths
export const getBasePathForCollection = (collectionName: string): string =>
  BASE_PATH_BY_COLLECTION[collectionName] ?? "";

export const getLink = (item: any, locale?: Locale): ILink => {
  if (!item) {
    return { href: "" };
  }
  const href = getBasePathForType(item.__typename);

  const cmsLocale = locale ? getCmsLocale(locale) : undefined;

  const resolveLocalizedSlug = (record: any): string | undefined => {
    if (!record) return undefined;
    if (cmsLocale && Array.isArray(record.slugs)) {
      const match = record.slugs.find(
        (s: { locale: string; value: string }) => s.locale === cmsLocale,
      );
      if (match?.value) return match.value;
    }
    return record.slug ?? undefined;
  };

  const getOfficeLeafSlug = (): string | undefined => resolveLocalizedSlug(item);

  // Get parent slug path — for offices, include the full chain (root → … → immediate parent)
  const getParentSlug = () => {
    if (!item.parent) return "";

    if (item.__typename === ModelTypes.OfficeRecord) {
      return getOfficeParentPathPrefix(item, cmsLocale);
    }
    if (item.parent.slugs && Array.isArray(item.parent.slugs) && item.parent.slugs.length > 0) {
      if (cmsLocale) {
        const m = item.parent.slugs.find(
          (s: { locale: string; value: string }) => s.locale === cmsLocale,
        );
        if (m?.value) return `/${m.value}`;
      }
      return `/${item.parent.slugs[0].value}`;
    }

    if (item.parent.slug) {
      return `/${item.parent.slug}`;
    }

    return "";
  };

  const isOverviewRecord = OVERVIEW_RECORD_TYPES.has(item.__typename);

  const resolvedLeaf = getOfficeLeafSlug();

  return {
    href,
    ...(resolvedLeaf &&
      !isOverviewRecord && {
        slug: `${getParentSlug()}/${resolvedLeaf}`,
      }),
  };
};

export const pathToHref = (locale: Locale, path: string) => {
  if (path.startsWith("http")) return path;
  const site = getSiteUrlForLocale(locale);
  return `${site}${path}`;
};

/**
 * Returns the localized path for a given ILink and locale using next-intl's getPathname.
 * Route keys are derived from routing.pathnames — no separate mapping table needed.
 */
export const getTranslatedHref = (link: ILink, locale: Locale): string => {
  if (link.href.startsWith("http")) return link.href;

  // Normalize empty string home path to "/" for routing.pathnames lookup
  const basePath = link.href || "/";
  const publicLocale = getPublicLocale(locale);

  if (!link.slug) {
    if (!routePathnames.has(basePath)) {
      return `/${publicLocale}${link.href}`;
    }
    return getPathname({ locale, href: basePath as any });
  }

  const slugStr = link.slug.startsWith("/") ? link.slug.slice(1) : link.slug;
  const slugSegments = slugStr.split("/").filter(Boolean);

  // Prefer catch-all variant if it exists in routing.pathnames
  const catchAllKey = basePath === "/" ? "/[...slug]" : `${basePath}/[...slug]`;
  if (routePathnames.has(catchAllKey)) {
    return getPathname({
      locale,
      href: { pathname: catchAllKey as any, params: { slug: slugSegments } },
    });
  }

  // Try single-segment variant
  const singleSlugKey = basePath === "/" ? "/[slug]" : `${basePath}/[slug]`;
  if (routePathnames.has(singleSlugKey)) {
    return getPathname({
      locale,
      href: { pathname: singleSlugKey as any, params: { slug: slugStr } },
    });
  }

  // Fallback
  if (basePath === "/") {
    return `/${publicLocale}/${slugStr}`;
  }
  return `/${publicLocale}${link.href}/${slugStr}`;
};

/**
 * On office pages, general contact links should include office/location query params for form prefill.
 */
export const withOfficePrefillOnGeneralContactLink = (
  link: ILink,
  prefill: OfficeContactPrefillParams | undefined | null,
): ILink => {
  if (!prefill?.officeSlug || !link.href) return link;
  const fullPath = `${link.href}${link.slug ?? ""}`;
  if (fullPath.split("?")[0] !== SLUGS.contactGeneral) return link;

  const updatedFullPath = appendOfficeContactQueryToHref(fullPath, prefill);
  const queryIndex = updatedFullPath.indexOf("?");

  if (link.slug) {
    const slugPath = link.slug.split("?")[0];
    const query = queryIndex !== -1 ? updatedFullPath.slice(queryIndex) : "";
    return { ...link, slug: `${slugPath}${query}` };
  }

  return { ...link, href: updatedFullPath };
};
